import { useState, useRef, useEffect } from 'react';
import { IoCloudUploadOutline } from 'react-icons/io5';
import { useFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { fetchWasteCodes } from '../../store/actions/wasteCode';
import { addConsent, fetchConsents } from '../../store/actions/consent';
import { Loader } from '../../utils/tools';
import { Form, Row, Col, Button } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sk } from 'date-fns/locale';
import { DISTRICT_OFFICE } from '../../utils/constants/district_office';
import { CONSENTS_TYPE } from '../../utils/constants/consents_type';
import { loadingCodes } from '../../utils/constants/loadingCodes';
import { validation } from './validationSchema';

const LOADING_CODE_OPTIONS = Object.entries(loadingCodes).map(([category, codes]) => ({
    label: category,
    options: codes.map((code) => ({ value: code.value, label: `${code.value} - ${code.label}` })),
}));

const ACCEPTED_TYPES = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];

const selectErrorStyles = (hasError) => ({
    control: (base, state) => ({
        ...base,
        borderColor: hasError ? '#dc3545' : state.isFocused ? '#86b7fe' : '#ced4da',
        boxShadow: hasError
            ? '0 0 0 0.25rem rgba(220,53,69,.25)'
            : state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
        '&:hover': { borderColor: hasError ? '#dc3545' : '#86b7fe' },
    }),
});

const AddConsent = ({ onClose }) => {
    const dispatch = useDispatch();
    const branches = useSelector(state => state.branch.data);
    const wasteCodes = useSelector(state => state.wasteCodes.data);
    const wasteCodesLoading = useSelector(state => state.wasteCodes.loading);

    useEffect(() => {
        dispatch(fetchWasteCodes());
    }, [dispatch]);

    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0] ?? null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        setSelectedFile(e.dataTransfer.files[0] ?? null);
    };

    const formik = useFormik({
        initialValues: {
            name: '',
            issuingAuthority: null,
            consentType: '',
            decisionNumber: '',
            validFrom: new Date(),
            validUntil: null,
            scopeType: 'tenant',
            scopeBranch: '',
            wasteCodes: [],
            loadingCodes: [],
        },
        validationSchema: validation,
        validateOnBlur: true,
        validateOnChange: false,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            dispatch(addConsent({ values, file: selectedFile }))
                .unwrap()
                .then(() => {
                    dispatch(fetchConsents());
                    resetForm();
                    setSelectedFile(null);
                    onClose();
                })
                .catch(() => {})
                .finally(() => setSubmitting(false));
        },
    });

    if (wasteCodesLoading) return <Loader />;

    return (
        <Form onSubmit={formik.handleSubmit}>
            <h2>Pridať súhlas</h2>
            <hr />

            <Row>
                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formName">
                        <Form.Label>Názov súhlasu *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Názov súhlasu"
                            {...formik.getFieldProps('name')}
                            isInvalid={formik.touched.name && !!formik.errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formIssuingAuthority">
                        <Form.Label>Vydávajúci orgán *</Form.Label>
                        <Select
                            options={DISTRICT_OFFICE}
                            value={formik.values.issuingAuthority}
                            onChange={(option) => formik.setFieldValue('issuingAuthority', option)}
                            onBlur={() => formik.setFieldTouched('issuingAuthority', true)}
                            placeholder="Vyhľadať alebo vybrať vydávajúci orgán..."
                            isClearable
                            noOptionsMessage={() => 'Žiadne výsledky'}
                            styles={selectErrorStyles(formik.touched.issuingAuthority && !!formik.errors.issuingAuthority)}
                        />
                        {formik.touched.issuingAuthority && formik.errors.issuingAuthority && (
                            <div className="invalid-feedback d-block">{formik.errors.issuingAuthority}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formConsentType">
                        <Form.Label>Typ súhlasu *</Form.Label>
                        <Form.Select
                            aria-label="Vyberte typ súhlasu"
                            {...formik.getFieldProps('consentType')}
                            isInvalid={formik.touched.consentType && !!formik.errors.consentType}
                        >
                            <option value="">Vyberte typ súhlasu:</option>
                            {CONSENTS_TYPE.map((type) => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.consentType}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formDecisionNumber">
                        <Form.Label>Číslo rozhodnutia *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="OU-NR-OSZP3-2026/000000"
                            {...formik.getFieldProps('decisionNumber')}
                            isInvalid={formik.touched.decisionNumber && !!formik.errors.decisionNumber}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors.decisionNumber}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                    <Form.Group className="mb-3" controlId="formValidFrom">
                        <Form.Label>Právoplatný od *</Form.Label>
                        <DatePicker
                            id="validFrom"
                            name="validFrom"
                            showIcon
                            toggleCalendarOnIconClick
                            selected={formik.values.validFrom}
                            onChange={(date) => formik.setFieldValue('validFrom', date)}
                            onBlur={() => formik.setFieldTouched('validFrom', true)}
                            dateFormat="dd.MM.yyyy"
                            className={`form-control${formik.touched.validFrom && formik.errors.validFrom ? ' is-invalid' : ''}`}
                            locale={sk}
                        />
                        {formik.touched.validFrom && formik.errors.validFrom && (
                            <div className="invalid-feedback d-block">{formik.errors.validFrom}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12} md={6}>
                    <Form.Group className="mb-3" controlId="formValidUntil">
                        <Form.Label>Platný do *</Form.Label>
                        <DatePicker
                            id="validUntil"
                            name="validUntil"
                            showIcon
                            toggleCalendarOnIconClick
                            selected={formik.values.validUntil}
                            onChange={(date) => formik.setFieldValue('validUntil', date)}
                            onBlur={() => formik.setFieldTouched('validUntil', true)}
                            dateFormat="dd.MM.yyyy"
                            className={`form-control${formik.touched.validUntil && formik.errors.validUntil ? ' is-invalid' : ''}`}
                            locale={sk}
                        />
                        {formik.touched.validUntil && formik.errors.validUntil && (
                            <div className="invalid-feedback d-block">{formik.errors.validUntil}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formWasteCodes">
                        <Form.Label>Kódy odpadov *</Form.Label>
                        <Select
                            isMulti
                            options={Array.isArray(wasteCodes) ? wasteCodes.map(w => ({
                                value: w._id,
                                label: `${w.wasteCode} - ${w.wasteName}`,
                            })) : []}
                            value={formik.values.wasteCodes}
                            onChange={(options) => formik.setFieldValue('wasteCodes', options ?? [])}
                            onBlur={() => formik.setFieldTouched('wasteCodes', true)}
                            placeholder="Vyhľadať a vybrať kódy odpadov..."
                            noOptionsMessage={() => 'Žiadne výsledky'}
                            filterOption={(option, input) => {
                                const normalized = input.replace(/\s/g, '').toLowerCase();
                                const label = option.label.replace(/\s/g, '').toLowerCase();
                                return label.includes(normalized);
                            }}
                            styles={selectErrorStyles(formik.touched.wasteCodes && !!formik.errors.wasteCodes)}
                        />
                        {formik.touched.wasteCodes && formik.errors.wasteCodes && (
                            <div className="invalid-feedback d-block">{formik.errors.wasteCodes}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formLoadingCodes">
                        <Form.Label>Kód nakladania *</Form.Label>
                        <Select
                            isMulti
                            options={LOADING_CODE_OPTIONS}
                            value={formik.values.loadingCodes}
                            onChange={(options) => formik.setFieldValue('loadingCodes', options ?? [])}
                            onBlur={() => formik.setFieldTouched('loadingCodes', true)}
                            placeholder="Vyhľadať a vybrať kód nakladania..."
                            noOptionsMessage={() => 'Žiadne výsledky'}
                            filterOption={(option, input) => {
                                const normalized = input.replace(/\s/g, '').toLowerCase();
                                const label = option.label.replace(/\s/g, '').toLowerCase();
                                return label.includes(normalized);
                            }}
                            styles={selectErrorStyles(formik.touched.loadingCodes && !!formik.errors.loadingCodes)}
                        />
                        {formik.touched.loadingCodes && formik.errors.loadingCodes && (
                            <div className="invalid-feedback d-block">{formik.errors.loadingCodes}</div>
                        )}
                    </Form.Group>
                </Col>

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formScopeType">
                        <Form.Label>Rozsah pôsobnosti</Form.Label>
                        <Form.Select
                            aria-label="Vyberte rozsah pôsobnosti"
                            {...formik.getFieldProps('scopeType')}
                        >
                            <option value="tenant">Celý subjekt</option>
                            <option value="branch">Konkrétna prevádzka</option>
                        </Form.Select>
                    </Form.Group>
                </Col>

                {formik.values.scopeType === 'branch' && (
                    <Col xs={12}>
                        <Form.Group className="mb-3" controlId="formScopeBranch">
                            <Form.Label>Prevádzka</Form.Label>
                            <Form.Select
                                aria-label="Vyberte prevádzku"
                                {...formik.getFieldProps('scopeBranch')}
                            >
                                <option value="">Vyberte prevádzku:</option>
                                {Array.isArray(branches) && branches.map((branch) => (
                                    <option key={branch._id} value={branch._id}>
                                        {branch.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}

                <Col xs={12}>
                    <Form.Group className="mb-3" controlId="formFile">
                        <Form.Label>Rozhodnutie (príloha)</Form.Label>
                        <div
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current.click()}
                            style={{
                                border: `2px dashed ${isDragOver ? '#86b7fe' : '#ced4da'}`,
                                borderRadius: '8px',
                                padding: '24px',
                                textAlign: 'center',
                                cursor: 'pointer',
                                backgroundColor: isDragOver ? '#f0f6ff' : '#fafafa',
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept={ACCEPTED_TYPES.join(',')}
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            {selectedFile ? (
                                <div>
                                    <IoCloudUploadOutline size={40} style={{ color: '#adb5bd', marginBottom: 4 }} />
                                    <div className="fw-semibold">{selectedFile.name}</div>
                                    <Form.Text className="text-muted">
                                        {(selectedFile.size / 1024).toFixed(1)} KB — kliknite pre zmenu
                                    </Form.Text>
                                </div>
                            ) : (
                                <div>
                                    <IoCloudUploadOutline size={40} style={{ color: '#adb5bd', marginBottom: 4 }} />
                                    <div className="fw-semibold">Pretiahnite súbor sem</div>
                                    <Form.Text className="text-muted">
                                        alebo kliknite pre výber — {ACCEPTED_TYPES.join(', ')}
                                    </Form.Text>
                                </div>
                            )}
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mt-auto">
                <hr />
                <Col className="d-flex justify-content-end">
                    <Button
                        className="button-custom-large"
                        variant="primary"
                        type="submit"
                        size="lg"
                        style={{ marginTop: 'auto' }}
                        disabled={formik.isSubmitting}
                    >
                        {formik.isSubmitting ? <Loader /> : 'Pridať súhlas'}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default AddConsent;
